import type {
  BrightnessId,
  FourTransformationEdge,
  NormalizedChart,
  NormalizedPalace,
  NormalizedStar,
  PalaceRelationEdge,
} from './domain.js';
import type { Feature } from './rules.js';

const SUPPORTIVE_BRIGHTNESS = new Set<BrightnessId>(['temple', 'prosperous', 'gain', 'favorable']);
const WEAKENED_BRIGHTNESS = new Set<BrightnessId>(['weak', 'fallen']);

function evidenceId(parts: readonly string[]): string {
  return ['NATAL', ...parts].join(':').toUpperCase();
}

function starFeatures(star: NormalizedStar, palace: NormalizedPalace): Feature[] {
  const base = `palace.${palace.id}.star.${star.id}`;
  const starEvidence = evidenceId(['PALACE', palace.id, 'STAR', star.id]);
  const brightnessEvidence = evidenceId(['PALACE', palace.id, 'STAR', star.id, 'BRIGHTNESS', star.brightness]);
  const features: Feature[] = [
    {
      id: `palace.${palace.id}.has_star.${star.id}`,
      scope: 'natal',
      kind: 'star_in_palace',
      value: true,
      evidenceRefs: [starEvidence],
      evidenceGroup: `${palace.id}-palace-stars`,
    },
    {
      id: `${base}.brightness`,
      scope: 'natal',
      kind: 'star_brightness',
      value: star.brightness,
      evidenceRefs: [brightnessEvidence],
      evidenceGroup: 'star-brightness',
    },
    {
      id: `star.${star.id}.brightness.${star.brightness}`,
      scope: 'natal',
      kind: 'star_brightness_flag',
      value: true,
      evidenceRefs: [brightnessEvidence],
      evidenceGroup: 'star-brightness',
    },
  ];

  if (SUPPORTIVE_BRIGHTNESS.has(star.brightness)) {
    features.push({
      id: `${base}.dignity.supportive`,
      scope: 'natal',
      kind: 'star_dignity_modifier',
      value: true,
      evidenceRefs: [brightnessEvidence],
      evidenceGroup: 'star-brightness',
    });
    features.push({
      id: `star.${star.id}.dignity.supportive`,
      scope: 'natal',
      kind: 'star_dignity_modifier',
      value: true,
      evidenceRefs: [brightnessEvidence],
      evidenceGroup: 'star-brightness',
    });
  }

  if (WEAKENED_BRIGHTNESS.has(star.brightness)) {
    features.push({
      id: `${base}.dignity.weakened`,
      scope: 'natal',
      kind: 'star_dignity_modifier',
      value: true,
      evidenceRefs: [brightnessEvidence],
      evidenceGroup: 'star-brightness',
    });
    features.push({
      id: `star.${star.id}.dignity.weakened`,
      scope: 'natal',
      kind: 'star_dignity_modifier',
      value: true,
      evidenceRefs: [brightnessEvidence],
      evidenceGroup: 'star-brightness',
    });
  }

  for (const mutagen of star.mutagens) {
    const transformationEvidence = evidenceId(['PALACE', palace.id, 'STAR', star.id, 'MUTAGEN', mutagen]);
    features.push({
      id: `palace.${palace.id}.star.${star.id}.birth_year_mutagen.${mutagen}`,
      scope: 'natal',
      kind: 'birth_year_four_transformation',
      value: true,
      evidenceRefs: [transformationEvidence],
      evidenceGroup: 'birth-year-transformations',
    });
    features.push({
      id: `palace.${palace.id}.birth_year_mutagen.${mutagen}`,
      scope: 'natal',
      kind: 'palace_birth_year_four_transformation',
      value: true,
      evidenceRefs: [transformationEvidence],
      evidenceGroup: 'birth-year-transformations',
    });
  }

  return features;
}

function relationFeature(edge: PalaceRelationEdge): Feature {
  return {
    id: `relation.${edge.from}.${edge.type}.${edge.to}`,
    scope: 'natal',
    kind: 'palace_relation',
    value: true,
    evidenceRefs: [evidenceId(['RELATION', edge.from, edge.type, edge.to])],
    evidenceGroup: 'palace-relations',
  };
}

function transformationFeature(edge: FourTransformationEdge): Feature {
  return {
    id: `palace_stem_flight.${edge.sourcePalaceId}.${edge.mutagen}.${edge.targetPalaceId}.${edge.starId}`,
    scope: edge.scope,
    kind: 'palace_stem_four_transformation_edge',
    value: true,
    evidenceRefs: [
      evidenceId([
        'PALACE-STEM-FLIGHT',
        edge.sourcePalaceId,
        edge.mutagen,
        edge.targetPalaceId,
        edge.starId,
      ]),
    ],
    evidenceGroup: 'palace-stem-transformations',
  };
}


function relatedPalaceStarFeatures(chart: NormalizedChart): Feature[] {
  const features: Feature[] = [];
  for (const edge of chart.relations) {
    if (edge.type !== 'trine' && edge.type !== 'opposite') continue;
    const target = chart.palaces[edge.to];
    if (!target) continue;
    for (const star of [...target.majorStars, ...target.minorStars, ...target.adjectiveStars]) {
      features.push({
        id: `palace.${edge.from}.${edge.type}.${edge.to}.has_star.${star.id}`,
        scope: 'natal',
        kind: 'related_palace_star',
        value: true,
        evidenceRefs: [
          evidenceId(['RELATION', edge.from, edge.type, edge.to]),
          evidenceId(['PALACE', edge.to, 'STAR', star.id]),
        ],
        evidenceGroup: `${edge.from}-surrounding-palaces`,
      });
    }
  }
  return features;
}

export function extractNatalFeatures(chart: NormalizedChart): Feature[] {
  const features: Feature[] = [];

  for (const palaceId of chart.palaceOrder) {
    const palace = chart.palaces[palaceId];
    if (!palace) continue;

    features.push({
      id: `palace.${palace.id}.major_star_count`,
      scope: 'natal',
      kind: 'palace_major_star_count',
      value: palace.majorStars.length,
      evidenceRefs: [evidenceId(['PALACE', palace.id, 'MAJOR-STAR-COUNT', String(palace.majorStars.length)])],
      evidenceGroup: `${palace.id}-palace-stars`,
    });

    features.push({
      id: `palace.${palace.id}.is_body_palace`,
      scope: 'natal',
      kind: 'body_palace_flag',
      value: palace.isBodyPalace,
      evidenceRefs: [evidenceId(['PALACE', palace.id, 'BODY', String(palace.isBodyPalace)])],
      evidenceGroup: 'body-palace',
    });

    for (const star of [...palace.majorStars, ...palace.minorStars, ...palace.adjectiveStars]) {
      features.push(...starFeatures(star, palace));
    }
  }

  features.push(...chart.relations.map(relationFeature));
  features.push(...relatedPalaceStarFeatures(chart));
  features.push(...chart.natalTransformations.map(transformationFeature));

  return features.sort((a, b) => a.id.localeCompare(b.id));
}
